import { computed, defineComponent, inject, ref } from "vue";
import { useI18n } from "vue-i18n";
import html_iway from "./template.iway.html?raw";
import Cart from "../../Shared/Cart";
import ErrorMessage from "../../Shared/ErrorMessage";
import CompletionVoucherCode from "../CompletionVoucherCode";
import CompletionReferrerCode from "../CompletionReferrerCode";
import { currentOrderProviderKey } from "../../../infrastructure/current_order_provider";
import { orderServiceKey } from "../../../infrastructure/gfo_api/order_service";
import { ApiValidationError } from "../../../infrastructure/gfo_api/api_validation_error";
import type { OrderContactDto } from "../../../infrastructure/gfo_api/order_api";
import { useRouter } from "vue-router";

const app_theme = import.meta.env.VITE_APP_THEME;
let html = "";
if ("iway" == app_theme) {
  html = html_iway;
} else if ("lic" == app_theme) {
  html = html_iway;
} else if ("sak" == app_theme) {
  html = html_iway;
}
export default defineComponent({
  template: html,
  components: { Cart, ErrorMessage, CompletionVoucherCode, CompletionReferrerCode },
  setup() {
    // Injections
    const router = useRouter();
    const { t } = useI18n();
    const currentOrderProvider = inject(currentOrderProviderKey);
    const orderService = inject(orderServiceKey);

    // vars und computed
    const order = computed(() => currentOrderProvider?.currentOrder?.value ?? null);
    const termsAccepted = ref(false);
    const generalError = ref("");
    const submitting = ref(false);
    const canSubmit = computed(() => termsAccepted.value && !submitting.value);

    // Generische Anzeige der bisher erfassten Config-Werte (config.data ist je nach
    // Config-Typ unterschiedlich aufgebaut) - zeigt einfach alle nicht-leeren Felder an.
    const configEntries = function (data: any): { key: string; value: string }[] {
      if (null == data || "object" != typeof data) {
        return [];
      }
      return Object.entries(data)
        .filter(([, value]) => null != value && "" !== value)
        .map(([key, value]) => ({
          key,
          value: "object" == typeof value ? JSON.stringify(value) : String(value),
        }));
    };

    // Formatiert eine Adresse als Zeilen für die reine Anzeige (keine leeren Felder)
    const addressLines = function (address: OrderContactDto | null): string[] {
      if (null == address) {
        return [];
      }
      const lines: string[] = [];
      if ("company" == address.contact_type && address.company_name) {
        lines.push(address.company_name);
      }
      const name = [address.first_name, address.last_name].filter((v) => v).join(" ");
      if (name) {
        lines.push(name);
      }
      const street = [address.street, address.building_number].filter((v) => v).join(" ");
      if (street) {
        lines.push(street);
      }
      const city = [address.zip, address.city].filter((v) => v).join(" ");
      if (city) {
        lines.push(city);
      }
      if (address.email) {
        lines.push(address.email);
      }
      if (address.phone) {
        lines.push(address.phone);
      }
      if (address.mobile) {
        lines.push(address.mobile);
      }
      return lines;
    };

    // methods
    const submit = async function () {
      if (orderService == null || submitting.value) {
        return;
      }
      generalError.value = "";
      if (!termsAccepted.value) {
        generalError.value = t("Completion_Terms_Required");
        return;
      }
      submitting.value = true;
      try {
        await orderService.submit();
        router.push("/confirmation");
      } catch (err) {
        if (err instanceof ApiValidationError) {
          generalError.value = err.title;
        } else {
          generalError.value = `${err}`;
        }
      } finally {
        submitting.value = false;
      }
    };

    const back = function () {
      router.push("/customerdata");
    };

    return {
      t,
      order,
      termsAccepted,
      generalError,
      submitting,
      canSubmit,
      configEntries,
      addressLines,
      submit,
      back,
    };
  },
} as any);
